// Centralized Dice sound manager — procedural one-shots + sampled dice roll loop.
import {
  buildProceduralBuffer,
  type ProceduralSoundKind,
} from './procedural.js';
import { loadDiceRollSample, ROLL_ANIMATION_MIN_SEC } from './roll-sample.js';

export type SoundId = ProceduralSoundKind | 'dice.rollLoop';

export interface SoundManagerOptions {
  createContext?: () => AudioContext;
  now?: () => number;
  fetchFn?: (url: string) => Promise<Response>;
}

interface LoopHandle {
  source: AudioBufferSourceNode;
  gain: GainNode;
}

const GROUP_GAIN: Record<'ui' | 'dice' | 'result', number> = {
  ui: 0.35,
  dice: 0.5,
  result: 0.55,
};

/** Per-sound gain multiplier within the dice group (roll loop is quieter). */
const SOUND_LEVEL: Partial<Record<SoundId, number>> = {
  'dice.rollLoop': 0.55,
};

const ROLL_LOOP_FADE_OUT_SEC = 0.08;

const SOUND_GROUP: Record<SoundId, 'ui' | 'dice' | 'result'> = {
  'ui.click': 'ui',
  'ui.select': 'ui',
  'dice.rollPress': 'dice',
  'dice.rollLoop': 'dice',
  'dice.land': 'dice',
  'result.lose': 'result',
  'result.win.small': 'result',
  'result.win.medium': 'result',
  'result.win.large': 'result',
};

let shared: SoundManager | undefined;

export function getSoundManager(): SoundManager {
  if (!shared) shared = new SoundManager();
  return shared;
}

/** Test hook: replace the process-wide singleton. */
export function setSoundManager(manager: SoundManager | undefined): void {
  shared = manager;
}

export class SoundManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private groups: Partial<Record<'ui' | 'dice' | 'result', GainNode>> = {};
  private buffers = new Map<SoundId, AudioBuffer>();
  private loops = new Map<SoundId, LoopHandle>();
  private enabled = true;
  private unlocked = false;
  private rollSampleFailed = false;
  private rollSampleLoading: Promise<AudioBuffer | null> | null = null;
  private readonly createContext: () => AudioContext;
  private readonly now: () => number;
  private readonly fetchFn: (url: string) => Promise<Response>;
  private rollLoopStarting = false;

  constructor(opts: SoundManagerOptions = {}) {
    this.createContext =
      opts.createContext ??
      (() => {
        const Ctx = (globalThis as typeof globalThis & { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
          .AudioContext ??
          (globalThis as typeof globalThis & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctx) throw new Error('AudioContext unavailable');
        return new Ctx();
      });
    this.now = opts.now ?? (() => Date.now());
    this.fetchFn = opts.fetchFn ?? fetch;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) this.stopAll();
  }

  /** Unlocks/resumes audio after a user gesture. Safe to call repeatedly. */
  async unlock(): Promise<void> {
    try {
      const ctx = await this.ensureContext();
      if (ctx.state === 'suspended') await ctx.resume();
      this.unlocked = true;
      void this.preloadRollSample();
    } catch {
      /* ignore — gameplay continues without audio */
    }
  }

  play(id: SoundId): void {
    if (!this.enabled || !this.unlocked) return;
    void this.playInternal(id).catch(() => {
      /* ignore playback errors */
    });
  }

  startLoop(id: SoundId): void {
    if (id !== 'dice.rollLoop') return;
    if (!this.enabled || !this.unlocked) return;
    if (this.loops.has(id) || this.rollLoopStarting || this.rollSampleFailed) return;
    this.rollLoopStarting = true;
    void this.startLoopInternal(id).finally(() => {
      this.rollLoopStarting = false;
    });
  }

  stopLoop(id: SoundId): void {
    const handle = this.loops.get(id);
    if (!handle) return;
    const ctx = this.ctx;
    const t0 = ctx ? ctx.currentTime : this.now() / 1000;
    try {
      handle.gain.gain.cancelScheduledValues(t0);
      handle.gain.gain.setValueAtTime(handle.gain.gain.value, t0);
      handle.gain.gain.linearRampToValueAtTime(0, t0 + ROLL_LOOP_FADE_OUT_SEC);
      handle.source.stop(t0 + ROLL_LOOP_FADE_OUT_SEC + 0.02);
      handle.source.disconnect();
      handle.gain.disconnect();
    } catch {
      /* already stopped */
    }
    this.loops.delete(id);
  }

  stopAll(): void {
    for (const id of [...this.loops.keys()]) this.stopLoop(id);
  }

  /** Suspends loops when the tab is hidden; does not change enabled preference. */
  onVisibilityHidden(): void {
    this.stopAll();
    if (this.ctx?.state === 'running') void this.ctx.suspend().catch(() => undefined);
  }

  /** Resumes context when tab becomes visible (still requires prior unlock). */
  async onVisibilityVisible(): Promise<void> {
    if (!this.unlocked || !this.enabled) return;
    try {
      if (this.ctx?.state === 'suspended') await this.ctx.resume();
    } catch {
      /* ignore */
    }
  }

  dispose(): void {
    this.stopAll();
    void this.ctx?.close();
    this.ctx = null;
    this.master = null;
    this.groups = {};
    this.buffers.clear();
    this.rollSampleLoading = null;
  }

  private async ensureContext(): Promise<AudioContext> {
    if (this.ctx) return this.ctx;
    this.ctx = this.createContext();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.85;
    this.master.connect(this.ctx.destination);
    for (const g of ['ui', 'dice', 'result'] as const) {
      const node = this.ctx.createGain();
      node.gain.value = GROUP_GAIN[g];
      node.connect(this.master);
      this.groups[g] = node;
    }
    return this.ctx;
  }

  /** Lazy-load the roll sample after first unlock (non-blocking). */
  private preloadRollSample(): void {
    if (this.rollSampleLoading || this.rollSampleFailed || this.buffers.has('dice.rollLoop')) return;
    void this.loadRollSampleBuffer();
  }

  private async loadRollSampleBuffer(): Promise<AudioBuffer | null> {
    const cached = this.buffers.get('dice.rollLoop');
    if (cached) return cached;
    if (this.rollSampleFailed) return null;
    if (!this.rollSampleLoading) {
      this.rollSampleLoading = (async () => {
        try {
          const ctx = await this.ensureContext();
          const buf = await loadDiceRollSample(ctx, this.fetchFn);
          if (!buf) {
            this.rollSampleFailed = true;
            return null;
          }
          this.buffers.set('dice.rollLoop', buf);
          return buf;
        } catch {
          this.rollSampleFailed = true;
          return null;
        } finally {
          this.rollSampleLoading = null;
        }
      })();
    }
    return this.rollSampleLoading;
  }

  private async buffer(id: SoundId): Promise<AudioBuffer | null> {
    if (id === 'dice.rollLoop') {
      return this.loadRollSampleBuffer();
    }
    const cached = this.buffers.get(id);
    if (cached) return cached;
    const ctx = await this.ensureContext();
    const buf = buildProceduralBuffer(ctx, id as ProceduralSoundKind);
    this.buffers.set(id, buf);
    return buf;
  }

  private groupNode(id: SoundId): GainNode | null {
    const g = SOUND_GROUP[id];
    return this.groups[g] ?? this.master;
  }

  private async playInternal(id: SoundId): Promise<void> {
    const ctx = await this.ensureContext();
    if (ctx.state === 'suspended') await ctx.resume();
    const buf = await this.buffer(id);
    if (!buf) return;
    const source = ctx.createBufferSource();
    source.buffer = buf;
    const gain = ctx.createGain();
    source.connect(gain);
    gain.connect(this.groupNode(id) ?? ctx.destination);
    source.start();
  }

  private async startLoopInternal(id: SoundId): Promise<void> {
    if (this.loops.has(id)) return;
    const ctx = await this.ensureContext();
    if (ctx.state === 'suspended') await ctx.resume();
    const buf = await this.buffer(id);
    if (!buf) {
      this.rollSampleFailed = true;
      return;
    }
    const source = ctx.createBufferSource();
    source.buffer = buf;
    source.loop = buf.duration < ROLL_ANIMATION_MIN_SEC;
    const gain = ctx.createGain();
    gain.gain.value = SOUND_LEVEL[id] ?? 1;
    source.connect(gain);
    gain.connect(this.groupNode(id) ?? ctx.destination);
    source.start();
    this.loops.set(id, { source, gain });
  }
}
